import React, { useState } from "react";
import { SkipBackIcon as Backspace, Lock, User } from "lucide-react";
import { z } from "zod/v3";
import { useStore } from "@tanstack/react-form";

import { Button } from "@workspace/ui/components/button";
import { Input } from "@workspace/ui/components/input";
import {
	revalidateLogic,
	useAppForm,
} from "@workspace/ui/components/tanstack-form";
import { cn } from "@workspace/ui/lib/utils";

const StaffLoginSchema = z.object({
	staffId: z.string().min(1, "Staff ID is required"),
	pin: z.string().length(4, "PIN must be exactly 4 digits"),
});

export type StaffLoginData = z.infer<typeof StaffLoginSchema>;

export function StaffLoginForm({
	onSubmit,
	onCancel,
}: {
	onSubmit: (data: StaffLoginData) => Promise<boolean>;
	onCancel?: () => void;
}) {
	const [activeField, setActiveField] = useState<"staffId" | "pin" | null>(
		"staffId",
	);

	const form = useAppForm({
		defaultValues: {
			staffId: "",
			pin: "",
		} as StaffLoginData,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: {
			onDynamic: StaffLoginSchema,
		},
		onSubmit: async ({ value, ...rest }) => {
			const result = await onSubmit(value);

			if (result) {
				rest.formApi.reset();
				setActiveField("staffId");
			} else {
				rest.formApi.reset();
				setActiveField("staffId");
			}
		},
	});

	const handleNumberClick = (number: string) => {
		if (activeField === "staffId") {
			form.setFieldValue("staffId", (prev) => prev + number);
		} else if (activeField === "pin") {
			form.setFieldValue("pin", (prev) =>
				prev.length < 4 ? prev + number : prev,
			);
		}

		form.validateAllFields("change");
	};

	const handleBackspace = () => {
		if (activeField === "staffId") {
			form.setFieldValue("staffId", (prev) => prev.slice(0, -1));
		} else if (activeField === "pin") {
			form.setFieldValue("pin", (prev) => {
				if (prev.length === 0) {
					setActiveField("staffId");
				}

				const n = prev.slice(0, -1);

				return n;
			});
		}
	};

	const handleClear = () => {
		if (activeField === "staffId") {
			form.setFieldValue("staffId", "");
		} else if (activeField === "pin") {
			form.setFieldValue("pin", "");
		}
	};

	const handleNext = () => {
		if (activeField === "staffId") {
			setActiveField("pin");
		}
	};

	const getButtonState = () => {
		if (activeField === "staffId") {
			return { text: "Next", action: "next", disabled: false };
		}

		if (activeField === "pin") {
			return {
				text: "Login",
				action: "submit",
			};
		}

		return { text: "Login", action: "submit", disabled: true };
	};

	const numbers = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];
	const buttonState = getButtonState();

	const validationState = useStore(form.store, (state) => state.errorMap);

	React.useEffect(() => {
		if (validationState.onDynamic?.staffId) {
			// eslint-disable-next-line react-hooks/set-state-in-effect -- HA-738: react-hooks 7.1 finding, refactor deferred
			setActiveField("staffId");
		}
	}, [validationState]);

	return (
		<>
			<div className="space-y-4">
				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
					className="space-y-4"
				>
					<div className="grid grid-cols-2 gap-2 items-start">
						<form.AppField name="staffId">
							{(field) => (
								<field.FormItem>
									<field.FormLabel className="flex items-center gap-2">
										<User className="size-4" />
										Staff ID
									</field.FormLabel>
									<field.FormControl>
										<Input
											id="staffId"
											name="staff-id-input"
											type="text"
											value={field.state.value}
											onChange={(e) => field.handleChange(e.target.value)}
											onBlur={() => field.handleBlur()}
											onFocus={() => setActiveField("staffId")}
											onMouseDown={(e) => e.preventDefault()}
											onClick={(e) => {
												e.preventDefault();
												setActiveField("staffId");
											}}
											placeholder="Enter staff ID"
											autoComplete="username"
											autoCorrect="off"
											autoCapitalize="off"
											spellCheck="false"
											data-lpignore="true"
											data-1p-ignore="true"
											data-bwignore="true"
											data-form-type="other"
											className={cn("text-center text-lg font-mono", {
												"ring-2 ring-primary": activeField === "staffId",
											})}
											readOnly
										/>
									</field.FormControl>
									<field.FormMessage />
								</field.FormItem>
							)}
						</form.AppField>
						<form.AppField name="pin">
							{(field) => (
								<field.FormItem>
									<field.FormLabel className="flex items-center gap-2 ">
										<Lock className="size-4 " />
										4-Digit PIN
									</field.FormLabel>
									<field.FormControl>
										<Input
											id="pin"
											name="staff-pin-input"
											type="password"
											value={field.state.value}
											onChange={(e) => field.handleChange(e.target.value)}
											onBlur={() => field.handleBlur()}
											onFocus={() => {
												setActiveField("pin");
											}}
											onMouseDown={(e) => e.preventDefault()}
											onClick={(e) => {
												setActiveField("pin");
												e.preventDefault();
											}}
											placeholder="••••"
											maxLength={4}
											autoComplete="new-password"
											autoCorrect="off"
											autoCapitalize="off"
											spellCheck="false"
											data-lpignore="true"
											data-1p-ignore="true"
											data-bwignore="true"
											data-form-type="other"
											className={cn(
												"text-center text-lg font-mono tracking-widest",
												{
													"ring-2 ring-primary": activeField === "pin",
												},
											)}
											readOnly
										/>
									</field.FormControl>
									<field.FormMessage />
									<div className="text-xs text-muted-foreground text-center">
										{field.state.value.length}/4 digits
									</div>
								</field.FormItem>
							)}
						</form.AppField>
					</div>

					{/* Number Pad */}
					<div className="grid gap-3">
						<div className="grid grid-cols-3 gap-3">
							{numbers.slice(0, 9).map((number) => (
								<Button
									key={number}
									type="button"
									variant="outline"
									size="lg"
									onClick={() => handleNumberClick(number)}
									disabled={!activeField}
									className="h-12 text-lg font-semibold"
								>
									{number}
								</Button>
							))}
						</div>

						<div className="grid grid-cols-3 gap-3">
							<Button
								type="button"
								variant="outline"
								size="lg"
								onClick={handleClear}
								disabled={!activeField}
								className="h-12 text-sm bg-transparent"
							>
								Clear
							</Button>

							<Button
								type="button"
								variant="outline"
								size="lg"
								onClick={() => handleNumberClick("0")}
								disabled={!activeField}
								className="h-12 text-lg font-semibold"
							>
								0
							</Button>

							<Button
								type="button"
								variant="outline"
								size="lg"
								onClick={handleBackspace}
								disabled={!activeField}
								className="h-12 bg-transparent"
							>
								<Backspace className="h-5 w-5 " />
							</Button>
						</div>
					</div>
					{/* Submit/Next Button */}
					{buttonState.action === "submit" && (
						<>
							<form.Subscribe
								selector={(state) => [
									state.canSubmit &&
										state.values.pin.length === 4 &&
										state.values.staffId.length > 0,
								]}
							>
								{([canSubmit]) => (
									<Button
										type="submit"
										className="w-full h-12 text-lg "
										disabled={buttonState.disabled || !canSubmit}
									>
										Login
									</Button>
								)}
							</form.Subscribe>
						</>
					)}
					{buttonState.action === "next" && (
						<>
							<Button
								type="button"
								onClick={handleNext}
								className="w-full h-12 text-lg "
								disabled={buttonState.disabled}
							>
								Next
							</Button>
						</>
					)}
					{onCancel && (
						<Button
							type="button"
							variant="outline"
							onClick={onCancel}
							className="w-full h-12 text-lg"
						>
							Cancel
						</Button>
					)}
				</form>
			</div>
		</>
	);
}
